import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { ApiClientError } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { safeNext } from '@/lib/safe-next';
import { RouteFallback } from './RouteFallback';

/** Screens for signed-in users. Handles signed-out, unverified and banned states. */
export function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <RouteFallback />;
  if (me.error instanceof ApiClientError) {
    if (me.error.code === 'EMAIL_NOT_VERIFIED') return <Navigate to="/verify-email" replace />;
    if (me.error.code === 'BANNED') return <Navigate to="/banned" replace />;
  }
  if (!me.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

/** Screens that need a finished profile; otherwise continue onboarding. */
export function RequireOnboarded({ children }: { children?: ReactNode }) {
  const me = useMe();
  if (!me.data) return <RouteFallback />;
  if (me.data.profile.onboardingStep !== 'done') return <Navigate to="/onboarding" replace />;
  return children ?? <Outlet />;
}

/** Sign in / sign up screens: signed-in users go on to where they were headed, or the app. */
export function PublicOnly() {
  const me = useMe();
  const [params] = useSearchParams();
  if (me.isPending) return <RouteFallback />;
  if (me.data) return <Navigate to={safeNext(params.get('next'))} replace />;
  return <Outlet />;
}

/** /app: route to the right place for this user. */
export function AppHome() {
  const me = useMe();
  if (!me.data) return <RouteFallback />;
  return (
    <Navigate
      to={me.data.profile.onboardingStep === 'done' ? '/discover' : '/onboarding'}
      replace
    />
  );
}
